import { isEqual } from "lodash";
import { useId, useState, forwardRef } from "react";

import { useCreateSwagContext } from "@contexts/create-swag-context";

import useManageDraftCollection from "@stadium/shared/src/hooks/catalog/use-manage-draft-collection";

import PreferredColors from "@components/create-swag/preferred-colors";
import { InternalButton as Button } from "@sharedComponents/buttons";

import { unwrapPromise } from "@stadium/helpers/utility";

import styles from "./main.module.scss";

const PreferredColorsDropdownBody = forwardRef(({ closePreferredColorsDropdown }, ref) => {
  const { draftSwagData, setDraftSwagData, preferredColorGroupIds } = useCreateSwagContext();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(preferredColorGroupIds);
  const bodyId = useId();
  const titleId = `${bodyId}-preferred-swag-colors-title`;
  const descriptionId = `${bodyId}-preferred-swag-colors-description`;

  const { saveDraftSwag } = useManageDraftCollection();

  const toggleProductColor = (colorId) => {
    if (selectedIds.includes(colorId)) {
      setSelectedIds(selectedIds.filter((id) => id !== colorId));
    } else {
      setSelectedIds([...selectedIds, colorId]);
    }
  };

  const handleDone = async () => {
    setIsSaving(true);
    const input = {
      colorGroupIds: selectedIds.map((id) => +id),
      id: draftSwagData.id,
    };

    const [catalog, err] = await unwrapPromise(saveDraftSwag(input));
    setIsSaving(false);
    if (err) {
      return;
    }
    setDraftSwagData({
      ...draftSwagData,
      colorGroupIds: catalog.colorGroupIds || selectedIds,
    });
    closePreferredColorsDropdown();
    return;
  };

  return (
    <div className={styles.navColorDropDown} ref={ref}>
      <div
        className={styles.colorDropDownContainer}
        role="region"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={0}
      >
        <header className={styles.headerWrpper}>
          <h2 className={styles.navTitle} id={titleId}>
            Preferred Swag Colors
          </h2>
          <Button
            styleType="no-style"
            onClick={closePreferredColorsDropdown}
            className={styles.closeButton}
            aria-label="Close dropdown"
          >
            <i className={styles.iconClose} aria-hidden="true" />
          </Button>
        </header>
        <p className={styles.navNote} id={descriptionId}>
          Select your preferred colors to filter the catalog and set default selections.
        </p>

        <section className={styles.preferredColorsContainer}>
          <PreferredColors onColorChange={toggleProductColor} selectedIds={selectedIds} />
          <Button
            isLoading={isSaving}
            onClick={() => handleDone()}
            styleType="primary"
            className={styles.doneBtn}
            disabled={isEqual(selectedIds, preferredColorGroupIds)}
            aria-label="Save preferred swag colors"
          >
            save
          </Button>
        </section>
      </div>
    </div>
  );
});

const PreferredColorsDropdownToggle = forwardRef(({ isOpen, openPreferredColorsDropdown }, ref) => {
  const { preferredColorNames } = useCreateSwagContext();
  const titleId = useId();

  const displayColors = preferredColorNames.slice(0, 4);
  const remainingCount = preferredColorNames.length > 4 ? preferredColorNames.length - 4 : 0;

  return (
    <div
      className={`${styles.colorDropdownWrapper} ${isOpen ? styles.clrDropDownOpen : ""}`}
      ref={ref}
    >
      <div className={styles.colorDropdownBlock}>
        <Button
          className={styles.colorDropdownBtn}
          styleType="none"
          onClick={openPreferredColorsDropdown}
          aria-labelledby={titleId}
        >
          <div className={styles.preferredColorsContainer}>
            <div>
              <span id={titleId} className={styles.preferredColorsTitle} aria-label="Preferred swag colors dropdown">
                Preferred swag colors
              </span>
              {isOpen ? (
                <div className={styles.selectedNumber}>{preferredColorNames.length} selected</div>
              ) : (
                <>
                  {preferredColorNames.length > 0 ? (
                    <div className={styles.preferredColorsList}>
                      {displayColors.map((color) => (
                        <div key={color.name}>
                          <div
                            key={color.name}
                            className={styles.preferredColorItem}
                            style={{ "--bg-color": color.name }}
                          ></div>
                        </div>
                      ))}

                      {remainingCount > 0 && (
                        <div className={styles.remainingCount}>+{remainingCount}</div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.selectedNumber}>0 Selected</div>
                  )}
                </>
              )}
            </div>
          </div>
        </Button>
      </div>
    </div>
  );
});

const PreferredColorsDropdown = {
  Body: PreferredColorsDropdownBody,
  Toggle: PreferredColorsDropdownToggle,
};

export default PreferredColorsDropdown;
